/**
 * Gestion › Présences › Garderie (écoles) : la semaine d'une école, depuis le
 * bureau (décidé le 01/11/2026 : « dans Présences je n'ai que les CDV »).
 *
 * L'administration et la coordination choisissent une école et une semaine ;
 * la base rend les cinq jours, les plages qui tournent chaque jour, les
 * enfants présents dans l'école cette semaine-là et leurs présences
 * (`garderie_semaine`). Une case se coche ou se décoche d'un clic
 * (`garderie_pointer`), au compte de qui l'a fait ; la base refuse l'avenir et
 * un mois facturé, l'écran le montre avant même de cliquer. Totaux par colonne
 * et par enfant, filtres (groupe, classe, nom, pointés seulement), vue d'un
 * seul jour pour pointer vite, export CSV.
 *
 * Le pointage sur le téléphone des animateurs reste le chemin du quotidien
 * (« Liens garderie ») ; ici on voit et on corrige. Pour pointer depuis
 * Gestion, « Pointer aujourd'hui » ouvre la liste du jour de l'école
 * (`PointageGarderie`), faite pour le téléphone.
 *
 * Une case : présent (✓ vert) ou rien. Correction du 02/11/2026 : sur un
 * téléphone, le survol reste collé à la dernière case touchée et montrait un ✓
 * pâle dans une case vide, qu'on prenait pour un troisième état ; et un doigt
 * posé pour faire défiler la grille cochait la case dessous. Le survol ne
 * s'affiche plus que sur un écran qui en a un, et un geste ne compte que s'il
 * est un appui franc (`useAppuiFranc`).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { AlertTriangle, Calendar, Check, CheckSquare, ChevronLeft, ChevronRight, Download, Loader2, Lock, School } from 'lucide-react'
import type { NavigationParams } from '../../App'
import { useAppuiFranc } from '../../../components/garderie/useAppuiFranc'
import {
  FILTRES_PAR_DEFAUT,
  appliquerLePointage,
  aujourdHuiIso,
  classesDeLaSemaine,
  colonnesDeLaSemaine,
  dateEnClair,
  decalerDeSemaines,
  estPresent,
  filtrerLesEnfants,
  jourCourt,
  lundiDe,
  semaineEnClair,
  semaineEnCsv,
  totalDeLEnfant,
  totauxDeLaSemaine,
  totauxParColonne,
  type ColonneDeLaGrille,
  type EnfantDeLaSemaine,
  type FiltresDeLaGrille,
  type SemaineDeGarderie,
} from '../../../lib/garderie/presences-bureau'
import { lireLaSemaine, lireLesEcoles, pointerDepuisLeBureau, type EcoleDeGarderie } from '../../../lib/garderie/presences-bureau.supabase'
import { messageDeRefus } from '../../../lib/garderie/pointage'

/* Le dernier choix (école, lundi), gardé le temps de la session du navigateur. */
const CLE_CHOIX = 'presences-garderie-choix'
const lireChoix = (): { ecoleId: string; lundi: string } | null => {
  try {
    const brut = sessionStorage.getItem(CLE_CHOIX)
    return brut ? (JSON.parse(brut) as { ecoleId: string; lundi: string }) : null
  } catch {
    return null
  }
}
const garderChoix = (ecoleId: string, lundi: string) => {
  try {
    sessionStorage.setItem(CLE_CHOIX, JSON.stringify({ ecoleId, lundi }))
  } catch {
    /* rien : on redemandera */
  }
}

/** Le message de la base sans son étiquette `[…]`. */
const messageLisible = (e: unknown): string => {
  const brut = e instanceof Error ? e.message : typeof e === 'object' && e && 'message' in e ? String((e as { message: unknown }).message) : String(e)
  if (/plage_pas_commencee:/.test(brut)) return messageDeRefus(brut)
  return brut.replace(/^\[[a-z-]+\]\s*/i, '')
}

type Vue = 'semaine' | 'jour'

export function PresencesGarderie({ onPointer }: { onPointer?: (params: NavigationParams) => void } = {}) {
  const [ecoles, setEcoles] = useState<EcoleDeGarderie[]>([])
  const [ecoleId, setEcoleId] = useState<string>(() => lireChoix()?.ecoleId ?? '')
  const [lundi, setLundi] = useState<string>(() => lireChoix()?.lundi ?? lundiDe(aujourdHuiIso()))
  const [semaine, setSemaine] = useState<SemaineDeGarderie | null>(null)
  const [chargement, setChargement] = useState(false)
  const [erreur, setErreur] = useState<string | null>(null)
  const [filtres, setFiltres] = useState<FiltresDeLaGrille>(FILTRES_PAR_DEFAUT)
  const [vue, setVue] = useState<Vue>('semaine')
  const [jourChoisi, setJourChoisi] = useState<string>('')
  const [enAttente, setEnAttente] = useState<Set<string>>(new Set())
  const requete = useRef(0)
  const { suivi, geste } = useAppuiFranc()

  useEffect(() => {
    lireLesEcoles()
      .then((e) => {
        setEcoles(e)
        setEcoleId((id) => (id && e.some((x) => x.centerId === id) ? id : e[0]?.centerId ?? ''))
      })
      .catch((e) => setErreur(messageLisible(e)))
  }, [])

  const charger = useCallback(async () => {
    if (!ecoleId || !lundi) return
    const n = ++requete.current
    setChargement(true)
    setErreur(null)
    try {
      const s = await lireLaSemaine(ecoleId, lundi)
      if (n !== requete.current) return
      setSemaine(s)
      setJourChoisi((j) => (j && s.jours.some((x) => x.date === j) ? j : s.jours.find((x) => x.date === s.aujourdHui)?.date ?? s.jours[0]?.date ?? ''))
    } catch (e) {
      if (n !== requete.current) return
      setSemaine(null)
      setErreur(messageLisible(e))
    } finally {
      if (n === requete.current) setChargement(false)
    }
  }, [ecoleId, lundi])

  useEffect(() => {
    void charger()
  }, [charger])

  useEffect(() => {
    if (ecoleId && lundi) garderChoix(ecoleId, lundi)
  }, [ecoleId, lundi])

  const colonnes = useMemo<ColonneDeLaGrille[]>(() => {
    if (!semaine) return []
    const jours = vue === 'jour' ? semaine.jours.filter((j) => j.date === jourChoisi) : semaine.jours
    return colonnesDeLaSemaine(semaine, jours)
  }, [semaine, vue, jourChoisi])

  const enfants = useMemo(() => (semaine ? filtrerLesEnfants(semaine.enfants, filtres) : []), [semaine, filtres])
  const classes = useMemo(() => (semaine ? classesDeLaSemaine(semaine.enfants) : []), [semaine])
  const totauxColonnes = useMemo(() => totauxParColonne(enfants, colonnes), [enfants, colonnes])
  const totaux = useMemo(() => (semaine ? totauxDeLaSemaine(semaine) : null), [semaine])

  const basculer = async (enfant: EnfantDeLaSemaine, c: ColonneDeLaGrille) => {
    if (!semaine || c.verrouillee) return
    const cle = `${enfant.id}|${c.cle}`
    if (enAttente.has(cle)) return
    const voulu = !estPresent(enfant, c.jour.date, c.plage.id)
    const avant = semaine
    setEnAttente((s) => new Set(s).add(cle))
    setSemaine(appliquerLePointage(semaine, enfant.id, c.jour.date, c.plage.id, voulu))
    setErreur(null)
    try {
      const present = await pointerDepuisLeBureau(enfant.id, c.jour.date, c.plage.id, voulu)
      setSemaine((s) => (s ? appliquerLePointage(s, enfant.id, c.jour.date, c.plage.id, present) : s))
    } catch (e) {
      setSemaine(avant)
      setErreur(`${enfant.prenom} ${enfant.nom}, ${c.plage.code} le ${dateEnClair(c.jour.date)} : ${messageLisible(e)}`)
    } finally {
      setEnAttente((s) => {
        const n = new Set(s)
        n.delete(cle)
        return n
      })
    }
  }

  const exporter = () => {
    if (!semaine) return
    const blob = new Blob(['﻿' + semaineEnCsv(semaine, enfants)], { type: 'text/csv;charset=utf-8' })
    const a = document.createElement('a')
    a.href = URL.createObjectURL(blob)
    a.download = `presences-garderie-${semaine.ecole.code}-${semaine.lundi}.csv`
    a.click()
    URL.revokeObjectURL(a.href)
  }

  const ecole = ecoles.find((e) => e.centerId === ecoleId) ?? null

  return (
    <div className="space-y-4">
      {/* L'école et la semaine */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-4 items-end">
          <label className="block">
            <span className="block text-sm font-medium text-gray-700 mb-2">
              <School className="h-4 w-4 inline mr-1" />
              École
            </span>
            <select
              value={ecoleId}
              onChange={(e) => setEcoleId(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              {ecoles.length === 0 && <option value="">Aucune école avec un catalogue de garderie</option>}
              {ecoles.map((e) => (
                <option key={e.centerId} value={e.centerId}>
                  {e.nom} — {e.anneeScolaire}-{e.anneeScolaire + 1}
                </option>
              ))}
            </select>
          </label>
          <div className="block">
            <span className="block text-sm font-medium text-gray-700 mb-2">
              <Calendar className="h-4 w-4 inline mr-1" />
              Semaine
            </span>
            <div className="flex items-center gap-1">
              <button type="button" onClick={() => setLundi((l) => decalerDeSemaines(l, -1))} className="rounded-lg border border-gray-300 p-2 hover:bg-gray-50" aria-label="Semaine précédente">
                <ChevronLeft className="h-4 w-4" />
              </button>
              <input
                type="date"
                value={lundi}
                onChange={(e) => e.target.value && setLundi(lundiDe(e.target.value))}
                className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
                aria-label="Une date de la semaine"
              />
              <button type="button" onClick={() => setLundi((l) => decalerDeSemaines(l, 1))} className="rounded-lg border border-gray-300 p-2 hover:bg-gray-50" aria-label="Semaine suivante">
                <ChevronRight className="h-4 w-4" />
              </button>
              <button type="button" onClick={() => setLundi(lundiDe(aujourdHuiIso()))} className="rounded-lg border border-gray-300 px-3 py-2 text-sm hover:bg-gray-50">
                Cette semaine
              </button>
            </div>
          </div>
        </div>
        <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
          <p className="text-xs text-gray-500">
            {semaineEnClair(lundi)}
            {ecole ? ` · ${ecole.nom}` : ''} · Un appui coche ou décoche, au compte de qui le fait ; l’avenir et un mois facturé ne se touchent pas.
          </p>
          {onPointer && ecoleId && (
            <button
              type="button"
              onClick={() => onPointer({ centerId: ecoleId, date: aujourdHuiIso() })}
              className="flex items-center gap-1.5 rounded-lg bg-primary-600 px-3 py-1.5 text-sm font-semibold text-white"
            >
              <CheckSquare className="h-4 w-4" /> Pointer aujourd’hui
            </button>
          )}
        </div>
        {erreur && (
          <div className="mt-3 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
            <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
            <span>{erreur}</span>
          </div>
        )}
      </div>

      {chargement && !semaine && (
        <div className="flex justify-center py-10">
          <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
        </div>
      )}

      {semaine && totaux && (
        <>
          {/* Les totaux */}
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2">
            <Tuile titre="Enfants" valeur={String(semaine.enfants.length)} sous="dans l’école" />
            <Tuile titre="Pointés" valeur={String(totaux.enfantsPointes)} sous={`${totaux.presences} présence(s)`} />
            {semaine.jours.map((j) => (
              <Tuile
                key={j.date}
                titre={jourCourt(j)}
                valeur={String(totaux.parJour.get(j.date) ?? 0)}
                sous={j.ferie ?? (j.etatMois === 'facture' ? 'mois facturé' : j.futur ? 'à venir' : j.etatMois === 'brouillon' ? 'brouillon' : 'ouvert')}
                accent={j.ferie ? 'amber' : j.etatMois === 'facture' ? 'gray' : undefined}
              />
            ))}
          </div>

          {/* Les filtres et la vue */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3 flex flex-wrap items-center gap-2">
            <div className="inline-flex rounded-lg border border-gray-300 overflow-hidden text-sm">
              <button type="button" onClick={() => setVue('semaine')} className={`px-3 py-1.5 ${vue === 'semaine' ? 'bg-primary-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}>
                Semaine
              </button>
              <button type="button" onClick={() => setVue('jour')} className={`px-3 py-1.5 border-l border-gray-300 ${vue === 'jour' ? 'bg-primary-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}>
                Un jour
              </button>
            </div>
            {vue === 'jour' && (
              <select value={jourChoisi} onChange={(e) => setJourChoisi(e.target.value)} className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm">
                {semaine.jours.map((j) => (
                  <option key={j.date} value={j.date}>
                    {jourCourt(j)}
                    {j.ferie ? ` · ${j.ferie}` : ''}
                  </option>
                ))}
              </select>
            )}
            <input
              type="search"
              value={filtres.recherche}
              onChange={(e) => setFiltres({ ...filtres, recherche: e.target.value })}
              placeholder="Nom, prénom, classe…"
              className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm min-w-[12rem]"
            />
            <select value={filtres.groupe} onChange={(e) => setFiltres({ ...filtres, groupe: e.target.value as FiltresDeLaGrille['groupe'] })} className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm">
              <option value="tous">Tous les groupes</option>
              <option value="maternelle">Maternelle</option>
              <option value="primaire">Primaire</option>
            </select>
            <select value={filtres.classe} onChange={(e) => setFiltres({ ...filtres, classe: e.target.value })} className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm">
              <option value="">Toutes les classes</option>
              {classes.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
            <label className="flex items-center gap-1.5 text-sm text-gray-700">
              <input type="checkbox" checked={filtres.pointesSeulement} onChange={(e) => setFiltres({ ...filtres, pointesSeulement: e.target.checked })} className="rounded border-gray-300" />
              Pointés seulement
            </label>
            <span className="ml-auto text-xs text-gray-500">
              {enfants.length} sur {semaine.enfants.length}
            </span>
            <button type="button" onClick={exporter} className="flex items-center gap-1 rounded-lg border border-gray-300 px-3 py-1.5 text-sm hover:bg-gray-50">
              <Download className="h-4 w-4" /> CSV
            </button>
            {chargement && <Loader2 className="h-4 w-4 animate-spin text-gray-400" />}
          </div>

          {/* La grille */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-x-auto">
            <table className="min-w-full text-sm border-separate border-spacing-0">
              <thead>
                <tr className="bg-gray-50 text-xs uppercase text-gray-500">
                  <th rowSpan={2} className="sticky left-0 z-10 bg-gray-50 px-3 py-2 text-left font-medium border-b border-r border-gray-200">
                    Enfant
                  </th>
                  {(vue === 'jour' ? semaine.jours.filter((j) => j.date === jourChoisi) : semaine.jours).map((j) => {
                    const n = colonnes.filter((c) => c.jour.date === j.date).length
                    if (n === 0) return null
                    return (
                      <th key={j.date} colSpan={n} className={`px-2 py-1.5 text-center font-medium border-b border-l border-gray-200 ${j.ferie ? 'text-amber-700' : j.etatMois === 'facture' || j.futur ? 'text-gray-400' : ''}`}>
                        {jourCourt(j)}
                        {j.ferie && <span className="block normal-case text-[10px] font-normal">{j.ferie}</span>}
                        {!j.ferie && j.etatMois === 'facture' && <span className="block normal-case text-[10px] font-normal">mois facturé</span>}
                      </th>
                    )
                  })}
                  <th rowSpan={2} className="px-3 py-2 text-right font-medium border-b border-l border-gray-200">
                    Total
                  </th>
                </tr>
                <tr className="bg-gray-50 text-xs text-gray-600">
                  {colonnes.map((c, i) => (
                    <th
                      key={c.cle}
                      title={`${c.plage.libelle} · ${c.plage.heureDebut}–${c.plage.heureFin}${c.motif ? ` · ${c.motif}` : ''}`}
                      className={`px-2 py-1 text-center font-normal border-b border-gray-200 ${i === 0 || colonnes[i - 1].jour.date !== c.jour.date ? 'border-l' : ''} ${c.verrouillee ? 'text-gray-400' : ''}`}
                    >
                      {vue === 'jour' ? (
                        <span>
                          {c.plage.libelle}
                          <span className="block text-[10px] text-gray-400">
                            {c.plage.heureDebut}–{c.plage.heureFin}
                          </span>
                        </span>
                      ) : (
                        c.plage.code
                      )}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {enfants.map((e) => (
                  <tr key={e.id} className="hover:bg-gray-50/60">
                    <td className="sticky left-0 z-10 bg-white px-3 py-1.5 border-b border-r border-gray-100">
                      <span className="font-medium text-gray-900">
                        {e.nom} {e.prenom}
                      </span>
                      <span className="ml-2 text-xs text-gray-500">
                        {e.classe ?? (e.groupe ?? '')}
                        {e.formule && <span className="ml-1 rounded-full bg-blue-50 px-1.5 text-blue-700">{e.formule}</span>}
                        {e.rentreLeMidi && <span className="ml-1 rounded-full bg-gray-100 px-1.5 text-gray-600">rentre le midi</span>}
                        {e.dateSortie && <span className="ml-1 rounded-full bg-amber-50 px-1.5 text-amber-700">sort le {dateEnClair(e.dateSortie)}</span>}
                      </span>
                    </td>
                    {colonnes.map((c, i) => {
                      const present = estPresent(e, c.jour.date, c.plage.id)
                      const attente = enAttente.has(`${e.id}|${c.cle}`)
                      return (
                        <td key={c.cle} className={`px-1 py-1 text-center border-b border-gray-100 ${i === 0 || colonnes[i - 1].jour.date !== c.jour.date ? 'border-l border-l-gray-200' : ''}`}>
                          <button
                            type="button"
                            disabled={c.verrouillee || attente}
                            {...suivi}
                            onClick={() => {
                              if (geste(`${e.id}|${c.cle}`)) void basculer(e, c)
                            }}
                            title={c.motif ?? `${present ? 'Décocher' : 'Cocher'} ${c.plage.code} le ${dateEnClair(c.jour.date)}`}
                            aria-pressed={present}
                            className={`inline-flex touch-manipulation select-none items-center justify-center rounded-md border transition-colors ${vue === 'jour' ? 'h-9 w-12' : 'h-7 w-8'} ${
                              present
                                ? 'border-green-600 bg-green-600 text-white [@media(hover:hover)]:hover:bg-green-700'
                                : c.verrouillee
                                  ? 'border-gray-100 bg-gray-50 text-gray-300 cursor-not-allowed'
                                  : 'border-gray-200 bg-white text-gray-400 [@media(hover:hover)]:hover:border-green-400'
                            }`}
                          >
                            {/* Présent : ✓ ; vide : rien — jamais un ✓ pâle, qui se lisait comme un troisième état. */}
                            {attente ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : present ? <Check className="h-4 w-4" /> : c.verrouillee ? <Lock className="h-3 w-3" /> : null}
                          </button>
                        </td>
                      )
                    })}
                    <td className="px-3 py-1.5 text-right tabular-nums text-gray-700 border-b border-l border-gray-200">{totalDeLEnfant(e)}</td>
                  </tr>
                ))}
                {enfants.length === 0 && (
                  <tr>
                    <td colSpan={colonnes.length + 2} className="px-3 py-8 text-center text-gray-500">
                      {semaine.enfants.length === 0 ? 'Aucun enfant dans l’école cette semaine-là.' : 'Aucun enfant ne correspond aux filtres.'}
                    </td>
                  </tr>
                )}
              </tbody>
              <tfoot>
                <tr className="bg-gray-50 text-xs font-semibold text-gray-700">
                  <td className="sticky left-0 z-10 bg-gray-50 px-3 py-2 border-r border-gray-200">Total ({enfants.length})</td>
                  {colonnes.map((c, i) => (
                    <td key={c.cle} className={`px-2 py-2 text-center tabular-nums ${i === 0 || colonnes[i - 1].jour.date !== c.jour.date ? 'border-l border-gray-200' : ''}`}>
                      {totauxColonnes.get(c.cle) ?? 0}
                    </td>
                  ))}
                  <td className="px-3 py-2 text-right tabular-nums border-l border-gray-200">{enfants.reduce((n, e) => n + totalDeLEnfant(e), 0)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </>
      )}
    </div>
  )
}

function Tuile({ titre, valeur, sous, accent }: { titre: string; valeur: string; sous?: string; accent?: 'amber' | 'gray' }) {
  const couleur = accent === 'amber' ? 'text-amber-700' : accent === 'gray' ? 'text-gray-400' : 'text-gray-900'
  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3">
      <p className="text-[11px] uppercase tracking-wide text-gray-500">{titre}</p>
      <p className={`text-xl font-bold tabular-nums ${couleur}`}>{valeur}</p>
      {sous && <p className="text-[11px] text-gray-500 truncate">{sous}</p>}
    </div>
  )
}
